import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import { stocktakeService } from "../../_services";
import { productAction } from "../../_actions/product.action";
import moment from 'moment';
import toast from '../shared/Toast';
import { Modal } from "react-bootstrap";
import { RxCross2 } from "react-icons/rx";
import { FaFilter, FaTags, FaSearch } from "react-icons/fa";
import Select from "react-select";

// Import components
import NotificationBell from "../notifications/NotificationBell";
import LocationPicker from '../multiLocation/LocationPicker';
import useWindowSize from "../new-service/useWindowSize";
import CancelSaveBtn from "../new-service/add-edit-service/CancelSaveBtn";
import DeleteConfirmationPopup from "../common/DeleteConfirmationPopup";
import BackButton from "../common/BackButton";

// Import utilities
import { formatDate, getUserInfo } from "./StocktakeUtils";

// Import styles
import "../new-service/CatalogueTopNav.css";
import "./StocktakeDetail.css";
import "../product/product.css";
import "../new-service/newservice.css";

/**
 * StocktakeDetail component for editing a stock take
 */
const StocktakeDetail = (props) => {
  const { stocktakeId } = useParams();
  const [stocktake, setStocktake] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [productsByCategory, setProductsByCategory] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [kebabOpen, setKebabOpen] = useState(false);
  const { width } = useWindowSize();
  
  // Search and filter state variables
  const [searchQuery, setSearchQuery] = useState("");
  const [searchCrossIcon, setSearchCrossIcon] = useState(false);
  const [filters, setFilters] = useState({
    categoryFilter: "",
    brandFilter: "",
    stockStatus: ""
  });
  // Temporary filters for the filter popup
  const [tempFilters, setTempFilters] = useState({
    categoryFilter: "",
    brandFilter: "",
    stockStatus: ""
  });
  const [filterPopup, setFilterPopup] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [resetFilterPayload, setResetFilterPayload] = useState(null);

  // Fetch stocktake details when component mounts
  useEffect(() => {
    if (stocktakeId) {
      fetchStocktakeDetails();
    }
    
    // Fetch categories and brands for filters
    listAllCategories();
    listAllBrands();
  }, [stocktakeId]);

  /**
   * Fetch stocktake details from API
   */
  const fetchStocktakeDetails = () => {
    setLoading(true);
    setIsFormChanged(false);
    
    console.log("Fetching stocktake details for ID:", stocktakeId);
    
    stocktakeService.getStocktakeById(stocktakeId)
      .then(response => {
        console.log("Stocktake detail API response:", response);
        
        // Check for success in the response
        // The backend returns { statusCode: 1, responseData: { status: 1, message: "...", data: {...} } }
        if (response.data &&
            ((response.data.statusCode === 1 && response.data.responseData) ||
             (response.data.status === 1))) {
          
          // Get the stocktake data from the response
          const stocktakeData = response.data.responseData?.data || response.data.data;
          
          if (stocktakeData) {
            console.log("Stocktake data:", stocktakeData);
            setStocktake(stocktakeData);
            
            // After getting the stocktake data, check if we need to apply filters
            if (searchQuery || filters.categoryFilter || filters.brandFilter || filters.stockStatus) {
              // If there are search or filter parameters, call searchStocktakeItems
              console.log("Applying search/filters after fetching stocktake");
              // Use a slight delay to ensure the stocktake data is fully processed
              setTimeout(() => {
                searchStocktakeItems();
              }, 50);
            } else {
              // If no filters, just organize the items
              console.log("No search/filters, organizing all items");
              organizeProductsByCategory(stocktakeData.items || []);
            }
          } else {
            console.error("Error: No stocktake data in response:", response.data);
            toast.error("Failed to load stock take details: No data in response");
          }
        } else {
          console.error("Error fetching stocktake details:", response.data);
          const errorMsg = response.data?.responseData?.message ||
                          response.data?.message ||
                          response.data?.error?.message ||
                          "Unknown error";
          toast.error("Failed to load stock take details: " + errorMsg);
        }
        setLoading(false);
      })
      .catch(error => {
        console.error("Error fetching stocktake details:", error);
        console.error("Error details:", error.response ? error.response.data : "No response data");
        console.error("Error status:", error.response ? error.response.status : "No status");
        console.error("Error message:", error.message);
        
        const errorMessage = error.response?.data?.message ||
                            error.response?.data?.responseData?.message ||
                            error.message ||
                            "Unknown error";
        toast.error("Failed to load stock take details: " + errorMessage);
        setLoading(false);
      });
  };
  
  /**
   * Search and filter stocktake items using API
   */
  const searchStocktakeItems = (searchParams = {}) => {
    // Don't set loading to true for every search to avoid UI freezing
    // Only set it for initial load or when explicitly needed
    const isInitialLoad = !searchParams.hasOwnProperty('search');
    if (isInitialLoad) {
      setLoading(true);
    }
    
    // Get search parameters - use direct values from params when available
    const searchValue = searchParams.search !== undefined ? searchParams.search : searchQuery;
    const categoryFilterValue = searchParams.categoryId !== undefined ? searchParams.categoryId : filters.categoryFilter;
    const brandFilterValue = searchParams.brandId !== undefined ? searchParams.brandId : filters.brandFilter;
    const stockStatusValue = searchParams.stockStatus !== undefined ? searchParams.stockStatus : filters.stockStatus;
    
    console.log("Searching stocktake items with params:", {
      search: searchValue,
      categoryId: categoryFilterValue,
      brandId: brandFilterValue,
      stockStatus: stockStatusValue
    });
    
    // Use requestAnimationFrame to ensure UI updates before filtering
    requestAnimationFrame(() => {
      try {
        if (stocktake) {
          // Get all items from stocktake
          const allItems = stocktake.items || [];
          console.log(`Total items before filtering: ${allItems.length}`);
          
          // Apply filters (client-side filtering)
          let filteredItems = [...allItems]; // Create a copy to avoid mutation issues
          
          // Apply search filter
          if (searchValue) {
            filteredItems = filteredItems.filter(item =>
              (item.productName || '').toLowerCase().includes(searchValue.toLowerCase())
            );
            console.log(`Items after search filter: ${filteredItems.length}`);
          }
          
          // Apply category filter
          if (categoryFilterValue) {
            console.log("Filtering by category:", categoryFilterValue);
            filteredItems = filteredItems.filter(item => {
              // Use categoryId property directly from stocktake items
              // Convert both to strings to ensure consistent comparison
              const itemCategoryId = item.categoryId ? item.categoryId.toString() : '';
              const categoryIdToCompare = categoryFilterValue ? categoryFilterValue.toString() : '';
                                    
              console.log(`Item ${item.productName} category ID: ${itemCategoryId}, comparing with: ${categoryIdToCompare}`);
              return itemCategoryId === categoryIdToCompare;
            });
            console.log(`Items after category filter: ${filteredItems.length}`);
          }
          
          // Apply brand filter
          if (brandFilterValue) {
            console.log("Filtering by brand:", brandFilterValue);
            filteredItems = filteredItems.filter(item => {
              // Use productBrand property from stocktake items
              // Convert both to strings to ensure consistent comparison
              const itemBrandId = item.productBrand ? item.productBrand.toString() : '';
              const brandIdToCompare = brandFilterValue ? brandFilterValue.toString() : '';
                                 
              console.log(`Item ${item.productName} brand ID: ${itemBrandId}, comparing with: ${brandIdToCompare}`);
              return itemBrandId === brandIdToCompare;
            });
            console.log(`Items after brand filter: ${filteredItems.length}`);
          }
          
          // Apply stock status filter
          if (stockStatusValue) {
            console.log("Filtering by stock status:", stockStatusValue);
            if (stockStatusValue === "inStock") {
              filteredItems = filteredItems.filter(item => {
                // Use counted value for stocktake items
                const quantity = typeof item.counted === 'number' ? item.counted : 0;
                return quantity > 0;
              });
            } else if (stockStatusValue === "outOfStock") {
              filteredItems = filteredItems.filter(item => {
                // Use counted value for stocktake items
                const quantity = typeof item.counted === 'number' ? item.counted : 0;
                return quantity <= 0;
              });
            }
            console.log(`Items after stock status filter: ${filteredItems.length}`);
          }
          
          // Organize the filtered items by category
          console.log(`Final filtered items count: ${filteredItems.length}`);
          organizeProductsByCategory(filteredItems);
        } else {
          console.error("Cannot filter: stocktake data is not available");
        }
      } catch (error) {
        console.error("Error filtering stocktake items:", error);
        toast.error("Failed to filter stock take items: " + error.message);
        
        // If filtering fails, use the original items
        if (stocktake) {
          organizeProductsByCategory(stocktake.items || []);
        }
      }
      
      if (isInitialLoad) {
        setLoading(false);
      }
    });
  };

  /**
   * Organize products by category
   * @param {Array} items - The items to organize, already filtered
   */
  const organizeProductsByCategory = (items) => {
    console.log("Organizing products by category, item count:", items.length);
    
    // Group items by categoryName
    const groupedItems = items.reduce((acc, item) => {
      // Extract category information, handling different data structures
      const categoryName = item.categoryName ||
                          (item.category && item.category.categoryName) ||
                          'Uncategorized';
                          
      const categoryId = item.categoryId ||
                        (item.category && (item.category._id || item.category.categoryId)) ||
                        'uncategorized';
      
      if (!acc[categoryName]) {
        acc[categoryName] = {
          categoryId,
          categoryName,
          products: []
        };
      }
      
      // Transform the item to match the expected structure
      const transformedItem = {
        ...item,
        productId: {
          _id: item.productId,
          productName: item.productName || 'Unknown Product'
        }
      };
      
      acc[categoryName].products.push(transformedItem);
      return acc;
    }, {});
    
    // Convert to array and sort by category name
    let categoriesArray = Object.values(groupedItems).sort((a, b) =>
      a.categoryName.localeCompare(b.categoryName)
    );
    
    // Always filter out categories with no products
    categoriesArray = categoriesArray.filter(category => category.products.length > 0);
    
    console.log("Organized categories:", categoriesArray.map(cat => ({
      name: cat.categoryName,
      productCount: cat.products.length
    })));
    
    setProductsByCategory(categoriesArray);
  };

  /**
   * Handle counted value change for a product
   */
  const handleCountedChange = (categoryIndex, productIndex, value) => {
    // Allow empty string while typing
    const inputValue = value === '' ? '' : parseFloat(value) || 0;
    
    setProductsByCategory(prevCategories => {
      const newCategories = [...prevCategories];
      newCategories[categoryIndex].products[productIndex].counted = inputValue;
      return newCategories;
    });
    
    setIsFormChanged(true);
  };

  /**
   * Handle blur event to convert empty strings to 0
   */
  const handleInputBlur = (categoryIndex, productIndex) => {
    setProductsByCategory(prevCategories => {
      const newCategories = [...prevCategories];
      const product = newCategories[categoryIndex].products[productIndex];
      
      if (product.counted === '') {
        product.counted = 0;
      }
      
      return newCategories;
    });
  };

  /**
   * Save stocktake changes
   */
  const saveStocktake = () => {
    setSaving(true);
    
    // Flatten products from all categories
    const items = productsByCategory.flatMap(category =>
      category.products.map(product => ({
        productId: product.productId._id,
        categoryId: product.categoryId?._id,
        variantId: product.variantId,
        variantLabel: product.variantLabel,
        expected: product.expected,
        counted: product.counted
      }))
    );
    
    const updatedStocktake = {
      stocktakeId,
      items,
      notes: stocktake.notes
    };
    
    console.log("Saving stocktake with data:", updatedStocktake);
    
    stocktakeService.updateStocktake(updatedStocktake)
      .then(response => {
        console.log("Update stocktake response:", response);
        
        // Check for success in the response
        if (response.data &&
            ((response.data.statusCode === 1 && response.data.responseData) ||
             (response.data.status === 1))) {
          
          toast.success("Stock take saved successfully");
          setIsFormChanged(false);
          fetchStocktakeDetails(); // Refresh data
        } else {
          console.error("Error saving stocktake:", response.data);
          const errorMsg = response.data?.responseData?.message ||
                          response.data?.message ||
                          response.data?.error?.message ||
                          "Unknown error";
          toast.error("Failed to save stock take: " + errorMsg);
        }
        setSaving(false);
      })
      .catch(error => {
        console.error("Error saving stocktake:", error);
        console.error("Error details:", error.response ? error.response.data : "No response data");
        console.error("Error status:", error.response ? error.response.status : "No status");
        console.error("Error message:", error.message);
        
        const errorMessage = error.response?.data?.message ||
                            error.response?.data?.responseData?.message ||
                            error.message ||
                            "Unknown error";
        toast.error("Failed to save stock take: " + errorMessage);
        setSaving(false);
      });
  };

  /**
   * Complete stocktake
   */
  const completeStocktake = () => {
    if (!isFormChanged) {
      // If no changes, complete directly
      finalizeStocktake();
    } else {
      // If there are unsaved changes, ask to save first
      if (window.confirm("You have unsaved changes. Do you want to save before completing?")) {
        setSaving(true);
        
        // Flatten products from all categories
        const items = productsByCategory.flatMap(category =>
          category.products.map(product => ({
            productId: product.productId._id,
            categoryId: product.categoryId?._id,
            variantId: product.variantId,
            variantLabel: product.variantLabel,
            expected: product.expected,
            counted: product.counted
          }))
        );
        
        const updatedStocktake = {
          stocktakeId,
          items,
          notes: stocktake.notes
        };
        
        console.log("Saving stocktake before completing with data:", updatedStocktake);
        
        stocktakeService.updateStocktake(updatedStocktake)
          .then(response => {
            console.log("Update stocktake response before completing:", response);
            
            // Check for success in the response
            if (response.data &&
                ((response.data.statusCode === 1 && response.data.responseData) ||
                 (response.data.status === 1))) {
              
              finalizeStocktake();
            } else {
              console.error("Error saving stocktake before completing:", response.data);
              const errorMsg = response.data?.responseData?.message ||
                              response.data?.message ||
                              response.data?.error?.message ||
                              "Unknown error";
              toast.error("Failed to save stock take: " + errorMsg);
              setSaving(false);
            }
          })
          .catch(error => {
            console.error("Error saving stocktake before completing:", error);
            console.error("Error details:", error.response ? error.response.data : "No response data");
            console.error("Error status:", error.response ? error.response.status : "No status");
            console.error("Error message:", error.message);
            
            const errorMessage = error.response?.data?.message ||
                                error.response?.data?.responseData?.message ||
                                error.message ||
                                "Unknown error";
            toast.error("Failed to save stock take: " + errorMessage);
            setSaving(false);
          });
      }
    }
  };

  /**
   * Finalize stocktake (mark as complete)
   */
  const finalizeStocktake = () => {
    const { user } = getUserInfo();
    
    const completeData = {
      stocktakeId,
      userId: user._id
    };
    
    console.log("Completing stocktake with data:", completeData);
    
    stocktakeService.completeStocktake(completeData)
      .then(response => {
        console.log("Complete stocktake response:", response);
        
        // Check for success in the response
        if (response.data &&
            ((response.data.statusCode === 1 && response.data.responseData) ||
             (response.data.status === 1))) {
          
          toast.success("Stock take completed successfully");
          navigateBackToList();
        } else {
          console.error("Error completing stocktake:", response.data);
          const errorMsg = response.data?.responseData?.message ||
                          response.data?.message ||
                          response.data?.error?.message ||
                          "Unknown error";
          toast.error("Failed to complete stock take: " + errorMsg);
        }
        setSaving(false);
      })
      .catch(error => {
        console.error("Error completing stocktake:", error);
        console.error("Error details:", error.response ? error.response.data : "No response data");
        console.error("Error status:", error.response ? error.response.status : "No status");
        console.error("Error message:", error.message);
        
        const errorMessage = error.response?.data?.message ||
                            error.response?.data?.responseData?.message ||
                            error.message ||
                            "Unknown error";
        toast.error("Failed to complete stock take: " + errorMessage);
        setSaving(false);
      });
  };

  /**
   * Handle cancel button click
   */
  const handleCancel = () => {
    if (isFormChanged) {
      if (window.confirm("You have unsaved changes. Are you sure you want to cancel?")) {
        navigateBackToList();
      }
    } else {
      navigateBackToList();
    }
  };

  /**
   * Show delete confirmation modal
   */
  const showDeleteConfirmation = () => {
    setShowDeleteModal(true);
  };

  /**
   * Hide delete confirmation modal
   */
  const hideDeleteModal = () => {
    setShowDeleteModal(false);
  };

  /**
   * Navigate back to the stocktake list, preserving the date range filter
   */
  const navigateBackToList = () => {
    const dateRange = props.location?.state?.dateRange;
    props.history.push('/stocktake', dateRange ? { dateRange } : undefined);
  };

  /**
   * Handle successful deletion
   */
  const handleDeleteSuccess = () => {
    hideDeleteModal();
    navigateBackToList();
  };

  /**
   * Calculate variance for a product
   */
  const calculateVariance = (expected, counted) => {
    return (counted - expected);
  };

  /**
   * Calculate variance percentage for a product
   */
  const calculateVariancePercentage = (expected, counted) => {
    if (expected === 0) return counted > 0 ? 100 : 0;
    return ((counted - expected) / expected) * 100;
  };

  /**
   * Fetch all categories
   */
  const listAllCategories = () => {
    console.log("Fetching all categories");
    
    props.dispatch(productAction.listProductCategories())
      .then((response) => {
        console.log("Categories response:", response);
        
        let categoryList = null;
        if (response?.data?.responseData?.categories) {
          categoryList = response.data.responseData.categories;
        } else if (response?.data?.responseData?.result) {
          categoryList = response.data.responseData.result;
        }
        
        if (categoryList) {
          setCategories(categoryList);
        }
      })
      .catch((err) => {
        console.log("Error fetching categories:", err);
      });
  };

  /**
   * Fetch all brands
   */
  const listAllBrands = () => {
    console.log("Fetching all brands");
    
    props.dispatch(productAction.listProductBrands())
      .then((response) => {
        console.log("Brands response:", response);
        
        let brandList = null;
        if (response?.data?.responseData?.brands) {
          brandList = response.data.responseData.brands;
        } else if (response?.data?.responseData?.result) {
          brandList = response.data.responseData.result;
        }
        
        if (brandList) {
          setBrands(brandList);
        }
      })
      .catch((err) => {
        console.log("Error fetching brands:", err);
      });
  };

  /**
   * Handle search input change
   */
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (value) {
      setSearchCrossIcon(true);
    } else {
      setSearchCrossIcon(false);
    }
    
    // Use a global variable to store the timeout ID to avoid issues with closures
    if (window.searchTimeoutId) {
      clearTimeout(window.searchTimeoutId);
    }
    
    // Debounce search to avoid too many operations
    window.searchTimeoutId = setTimeout(() => {
      console.log("Searching with value:", value);
      // Call search function with current value
      searchStocktakeItems({ search: value });
      window.searchTimeoutId = null;
    }, 300);
  };

  /**
   * Clear search input
   */
  const handleResetSearchChange = () => {
    setSearchQuery("");
    setSearchCrossIcon(false);
    
    // Call API with empty search parameter
    searchStocktakeItems({ search: "" });
  };

  /**
   * Handle paste in search input
   */
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    
    if (pastedData !== '') {
      setSearchCrossIcon(true);
      setSearchQuery(pastedData);
      
      // Call API with pasted search text
      searchStocktakeItems({ search: pastedData });
    }
  };

  /**
   * Handle filter button click
   */
  const filterClick = () => {
    // Initialize temporary filters with current filter values
    // Ensure we're using empty strings instead of undefined or null
    setTempFilters({
      categoryFilter: filters.categoryFilter || "",
      brandFilter: filters.brandFilter || "",
      stockStatus: filters.stockStatus || ""
    });
    setFilterPopup(true);
    setAnimate(true);
  };
  
  /**
   * Handle reset filters
   */
  const handleResetFilters = (filterName) => {
    console.log("Resetting filter:", filterName);
    
    // Create a copy of the current filters
    const resetFilters = { ...filters };
    
    // Reset the specified filter
    if (filterName === 'categoryFilter') {
      resetFilters.categoryFilter = "";
    }
    else if (filterName === 'brandFilter') {
      resetFilters.brandFilter = "";
    }
    else if (filterName === 'stockStatus') {
      resetFilters.stockStatus = "";
    }
    else if (filterName === 'all') {
      // Reset all filters
      resetFilters.categoryFilter = "";
      resetFilters.brandFilter = "";
      resetFilters.stockStatus = "";
    }
    
    // Update filters state
    setFilters(resetFilters);
    
    // Also update temp filters if the filter popup is open
    if (filterPopup) {
      if (filterName === 'all') {
        // Reset all temp filters
        setTempFilters({
          categoryFilter: "",
          brandFilter: "",
          stockStatus: ""
        });
      } else {
        // Reset only the specified filter
        setTempFilters({
          ...tempFilters,
          [filterName]: ""
        });
      }
    }
    
    // Call search with updated filters
    searchStocktakeItems({
      categoryId: resetFilters.categoryFilter,
      brandId: resetFilters.brandFilter,
      stockStatus: resetFilters.stockStatus
    });
  };
  
  /**
   * Handle apply filters
   */
  const handleApplyFilters = (selectedFilters) => {
    console.log("Applying filters:", selectedFilters);
    
    // Create a new filters object to ensure state update
    const updatedFilters = {
      ...filters,
      categoryFilter: selectedFilters.categoryFilter || "",
      brandFilter: selectedFilters.brandFilter || "",
      stockStatus: selectedFilters.stockStatus || ""
    };
    
    // Update filters state
    setFilters(updatedFilters);
    setAnimate(false);
    
    // Close the filter popup with animation
    setTimeout(() => {
      setFilterPopup(false);
    }, 300);
    
    // Call search with updated filters
    searchStocktakeItems({
      categoryId: updatedFilters.categoryFilter,
      brandId: updatedFilters.brandFilter,
      stockStatus: updatedFilters.stockStatus
    });
  };

  /**
   * Handle close for popups
   */
  const handleClose = () => {
    setAnimate(false);
    setTimeout(() => {
      setFilterPopup(false);
    }, 300);
  };

  // Animation effect for popups
  useEffect(() => {
    if (filterPopup) {
      setAnimate(true);
    }
  }, [filterPopup]);

  // Add body class for popups
  useEffect(() => {
    if (filterPopup) {
      document.body.classList.add('filter_modal_body');
    }
    
    return () => {
      document.body.classList.remove('filter_modal_body');
    };
  }, [filterPopup]);

  const isCompleted = stocktake?.status === 1;

  return (
    <div className="h_n _new__product catalogue-no-header">
      <div className="body_container cm_bg_white _nowhite">
        <div className="catalogue-top-nav">
          <div className="catalogue-top-nav-container">
            <div className="catalogue-top-nav-content stocktake-top-nav-content">
              {/* Mobile-only: back arrow + title + kebab */}
              <div className="stocktake-top-nav-mobile">
                <BackButton onClick={handleCancel} ariaLabel="Back" />
                <h1 className="stocktake-top-nav-title">Stock Take</h1>
                {!isCompleted && (
                  <div className="stocktake-kebab-wrap">
                    <button
                      type="button"
                      className="stocktake-kebab-btn"
                      onClick={() => setKebabOpen(o => !o)}
                      aria-label="More options"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="5" r="1" />
                        <circle cx="12" cy="12" r="1" />
                        <circle cx="12" cy="19" r="1" />
                      </svg>
                    </button>
                    {kebabOpen && (
                      <>
                        <div className="stocktake-kebab-backdrop" onClick={() => setKebabOpen(false)} />
                        <div className="stocktake-kebab-menu">
                          <button
                            type="button"
                            className="stocktake-kebab-item stocktake-kebab-item--danger"
                            onClick={() => { setKebabOpen(false); showDeleteConfirmation(); }}
                          >
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Desktop-only: back icon + title + action button row */}
              <div className="stocktake-top-nav-desktop">
                <BackButton
                  onClick={handleCancel}
                  disabled={saving}
                  ariaLabel="Back to stock takes"
                />
                <h1 className="stocktake-top-nav-title">Stock Take</h1>
                <div className="stocktake-top-nav-actions">
                  {!isCompleted && (
                    <button
                      type="button"
                      className="stocktake-nav-btn stocktake-nav-btn--danger"
                      onClick={showDeleteConfirmation}
                      disabled={saving}
                    >
                      Delete
                    </button>
                  )}
                  {!isCompleted && (
                    <button
                      type="button"
                      className="stocktake-nav-btn stocktake-nav-btn--neutral"
                      onClick={saveStocktake}
                      disabled={saving || !isFormChanged}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  )}
                  {!isCompleted && (
                    <button
                      type="button"
                      className="stocktake-nav-btn stocktake-nav-btn--primary"
                      onClick={completeStocktake}
                      disabled={saving}
                    >
                      Complete
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="notif-bell-pinned-wrap">
            <LocationPicker />
            <NotificationBell />
          </div>
        </div>
        <div className="new-setting-wrapper">
          <div className="container">
            <div className="row">
              <div className="col-md-12">
                <div className="main_inner_wrapper_content">
                  <div className="main_wrapper_content_inner">
                    <div className="stocktake-detail-container">
                      
                      {/* Stock Take Title and Date */}
                      <div className="stocktake-header">
                        <div className="stocktake-title">
                          <h2>{stocktake?.name || 'Stock Take'}</h2>
                        </div>
                        <div className="stocktake-date">
                          {loading ? (
                            <p>Loading...</p>
                          ) : (
                            <p>{stocktake ? formatDate(stocktake.date) : 'N/A'}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Stock Take Status */}
                      <div className="stocktake-status">
                        <span className={`status-badge ${stocktake?.status === 1 ? 'completed' : 'in-progress'}`}>
                          {stocktake?.status === 1 ? 'Completed' : 'In Progress'}
                        </span>
                        {stocktake?.lastEditTime && (
                          <span className="last-edit">
                            Last edited: {moment(stocktake.lastEditTime).format('HH:mm [on] DD.MM.YY')}
                          </span>
                        )}
                      </div>
                      
                      {/* Notes */}
                      <div className="stocktake-notes">
                        <h3>Notes</h3>
                        <p>{stocktake?.notes || 'No notes'}</p>
                      </div>
                      
                      {/* Search and Filter Bar - Moved below notes section */}
                      <div className="search_filter_container mt-4">
                        <div className="_left">
                          <div className="cm_search_wrapper client_search m-0 ">
                            <input
                              type="text"
                              className="form-control"
                              name="productSearch"
                              value={searchQuery}
                              onChange={handleSearchChange}
                              onPaste={handlePaste}
                              placeholder="Search for a product"
                            />
                            {searchCrossIcon ?
                              <i><RxCross2 onClick={handleResetSearchChange}/></i>
                              : <i><FaSearch /></i>
                            }
                          </div>
                          <div className="_filter" onClick={filterClick}>
                            {width > 991 ? 'Filters' : <FaFilter />}
                          </div>
                        </div>
                      </div>
                      
                      {/* Applied Filters */}
                      {(filters?.categoryFilter || filters?.brandFilter || filters?.stockStatus) && (
                        <div className="applied_filter_container">
                          {filters.categoryFilter && (
                            <span>
                              {/* Find and display the category name instead of ID */}
                              {categories.find(cat =>
                                (cat._id || cat.categoryId) === filters.categoryFilter
                              )?.categoryName || 'Category'}
                              <i className="_cross" onClick={() => handleResetFilters('categoryFilter')}><RxCross2 /></i>
                            </span>
                          )}
                          {filters.brandFilter && (
                            <span>
                              {/* Find and display the brand name instead of ID */}
                              {brands.find(brand =>
                                (brand._id || brand.brandId) === filters.brandFilter
                              )?.brandName || 'Brand'}
                              <i className="_cross" onClick={() => handleResetFilters('brandFilter')}><RxCross2 /></i>
                            </span>
                          )}
                          {filters.stockStatus && (
                            <span>
                              {filters.stockStatus === "inStock" ? 'In Stock' : 'Out of Stock'}
                              <i className="_cross" onClick={() => handleResetFilters('stockStatus')}><RxCross2 /></i>
                            </span>
                          )}
                        </div>
                      )}
                      
                      {/* Loading indicator */}
                      {loading ? (
                        <div className="loading-container">
                          <p>Loading stock take data...</p>
                        </div>
                      ) : (
                        <>
                          {/* Products by Category */}
                          <div className="stocktake-products _new__product">
        
                            
                            {productsByCategory.length === 0 ? (
                              <div className="no-products">
                                <p>No products found in this stock take.</p>
                              </div>
                            ) : (
                              productsByCategory.map((category, categoryIndex) => (
                                <div key={category.categoryId} className="product-category">
                                  <div className="category-header">
                                    <div className="_first">
                                      <h2>{category.categoryName}</h2>
                                    </div>
                                  </div>
                                  
                                  <div className="product-items">
                                    {category.products.map((product, productIndex) => {
                                      const variance = calculateVariance(product.expected, product.counted);
                                      const variancePercentage = calculateVariancePercentage(product.expected, product.counted);
                                      const varianceClass =
                                        variance === 0 ? 'balanced' :
                                        variance > 0 ? 'over' : 'under';
                                      
                                      // Get the first letter of the product name for the placeholder
                                      const getInitial = () => {
                                        const name = product.productId.productName || product.productName || 'Unknown';
                                        return name.charAt(0).toUpperCase();
                                      };
                                      
                                      // Get a color based on the product name
                                      const getColorClass = () => {
                                        const colors = ['b', 's', 't']; // Blue, Salmon, Teal
                                        const name = product.productId.productName || product.productName || 'Unknown';
                                        const index = name.length % colors.length;
                                        return colors[index];
                                      };
                                      
                                      return (
                                        <div key={`${product.productId._id}:${product.variantId || ''}`} className="product-item">
                                          {/* Left side - Product icon/image */}
                                          {product.productImage ? (
                                            <div className="product-image">
                                              <img
                                                src={product.productImage}
                                                alt={product.productId.productName || product.productName}
                                                onError={(e) => {
                                                  e.target.style.display = 'none';
                                                  e.target.parentNode.innerHTML = `
                                                    <div class="${`product-initial-square ${getColorClass()}`}">
                                                      ${getInitial()}
                                                    </div>
                                                  `;
                                                }}
                                              />
                                            </div>
                                          ) : (
                                            <div className={`product-initial-square ${getColorClass()}`}>
                                              {getInitial()}
                                            </div>
                                          )}
                                          
                                          {/* Middle - Product name */}
                                          <div className="_text">
                                            <div className="product-header">
                                              <h3>{product.productId.productName || product.productName || 'Unknown Product'}</h3>
                                              {product.barcode && (
                                                <div className="product-details">
                                                  Barcode: {product.barcode}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                          
                                          {/* Right side - Stock take controls */}
                                          <div className="stocktake-controls">
                                            <div className="stocktake-row">
                                              <div className="expected-label">
                                                <span
                                                  className="label-text"
                                                  title={`Expected Stock Value: £${((product.stockPrice || 0) * product.expected).toFixed(2)}\nExpected Retail Value: £${((product.retailPrice || 0) * product.expected).toFixed(2)}`}
                                                >
                                                  Expected:
                                                </span> <span className="expected-value">{product.expected}</span>
                                              </div>
                                              
                                              <div className="counted-label">
                                                <span
                                                  className="label-text"
                                                  title={`Counted Stock Value: £${((product.stockPrice || 0) * (product.counted || 0)).toFixed(2)}\nCounted Retail Value: £${((product.retailPrice || 0) * (product.counted || 0)).toFixed(2)}`}
                                                >
                                                  Counted:
                                                </span>
                                                <input
                                                  type="number"
                                                  value={product.counted}
                                                  onChange={(e) => handleCountedChange(categoryIndex, productIndex, e.target.value)}
                                                  onBlur={() => handleInputBlur(categoryIndex, productIndex)}
                                                  disabled={stocktake?.status === 1}
                                                  className="counted-input"
                                                />
                                              </div>
                                              
                                              <div className={`variance-badge ${varianceClass}`}>
                                                {variance > 0 ? '↑' : variance < 0 ? '↓' : '✓'}{Math.abs(variance)}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                          
                          {/* Mobile-only bottom action bar — Save + Complete */}
                          {!isCompleted && width < 1101 && (
                            <div className="stocktake-mobile-action-bar">
                              <button
                                type="button"
                                className="stocktake-nav-btn stocktake-nav-btn--neutral stocktake-mobile-action"
                                onClick={saveStocktake}
                                disabled={saving || !isFormChanged}
                              >
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                type="button"
                                className="stocktake-nav-btn stocktake-nav-btn--primary stocktake-mobile-action"
                                onClick={completeStocktake}
                                disabled={saving}
                              >
                                Complete
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter popup */}
        <Modal
          blurClass="blure_bg"
          centered
          show={filterPopup}
          onHide={handleClose}
          backdropClassName="_light_bg"
          size="lg"
          dialogClassName={`bottom-modal ${animate ? 'slide-in' : 'slide-out'}`}
          className='_service__popup'
        >
          <Modal.Body>
            <div className="filters-popup">
              <div className="filter-group">
                <label>Category</label>
                <Select
                  menuPlacement="bottom"
                  classNamePrefix="my-select"
                  value={tempFilters.categoryFilter ? {
                    value: tempFilters.categoryFilter,
                    label: categories.find(cat => (cat._id || cat.categoryId) === tempFilters.categoryFilter)?.categoryName || 'All Categories'
                  } : null}
                  onChange={(selected) => setTempFilters({...tempFilters, categoryFilter: selected ? selected.value : ""})}
                  options={[
                    { value: "", label: "All Categories" },
                    ...categories.map(category => ({
                      value: category._id || category.categoryId,
                      label: category.categoryName
                    }))
                  ]}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      borderColor: '#707070',
                      boxShadow: 'none',
                      borderRadius: '9px',
                      minHeight: '44px',
                      '&:hover': {
                        borderColor: '#707070',
                      },
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected ? '#29282e' : 'white',
                      color: state.isSelected ? 'white' : '#333',
                      '&:hover': {
                        backgroundColor: state.isSelected ? '#29282e' : '#f0f0f0',
                      },
                    }),
                  }}
                  placeholder="All Categories"
                  isSearchable={false}
                />
              </div>
              
              <div className="filter-group">
                <label>Brand</label>
                <Select
                  menuPlacement="bottom"
                  classNamePrefix="my-select"
                  value={tempFilters.brandFilter ? {
                    value: tempFilters.brandFilter,
                    label: brands.find(brand => (brand._id || brand.brandId) === tempFilters.brandFilter)?.brandName || 'All Brands'
                  } : null}
                  onChange={(selected) => setTempFilters({...tempFilters, brandFilter: selected ? selected.value : ""})}
                  options={[
                    { value: "", label: "All Brands" },
                    ...brands.map(brand => ({
                      value: brand._id || brand.brandId,
                      label: brand.brandName
                    }))
                  ]}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      borderColor: '#707070',
                      boxShadow: 'none',
                      borderRadius: '9px',
                      minHeight: '44px',
                      '&:hover': {
                        borderColor: '#707070',
                      },
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected ? '#29282e' : 'white',
                      color: state.isSelected ? 'white' : '#333',
                      '&:hover': {
                        backgroundColor: state.isSelected ? '#29282e' : '#f0f0f0',
                      },
                    }),
                  }}
                  placeholder="All Brands"
                  isSearchable={false}
                />
              </div>
              
              <div className="filter-group">
                <label>Stock Status</label>
                <Select
                  menuPlacement="bottom"
                  classNamePrefix="my-select"
                  value={tempFilters.stockStatus ? {
                    value: tempFilters.stockStatus,
                    label: tempFilters.stockStatus === "inStock" ? "In Stock" : "Out of Stock"
                  } : null}
                  onChange={(selected) => setTempFilters({...tempFilters, stockStatus: selected ? selected.value : ""})}
                  options={[
                    { value: "", label: "All" },
                    { value: "inStock", label: "In Stock" },
                    { value: "outOfStock", label: "Out of Stock" }
                  ]}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      borderColor: '#707070',
                      boxShadow: 'none',
                      borderRadius: '9px',
                      minHeight: '44px',
                      '&:hover': {
                        borderColor: '#707070',
                      },
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected ? '#29282e' : 'white',
                      color: state.isSelected ? 'white' : '#333',
                      '&:hover': {
                        backgroundColor: state.isSelected ? '#29282e' : '#f0f0f0',
                      },
                    }),
                  }}
                  placeholder="All"
                  isSearchable={false}
                />
              </div>
              
              <div className="filter-actions text-right mt-5">
                <button
                  className="btn btn-outline-dark pl-4 pr-4"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-dark ml-4 pl-4 pr-4"
                  onClick={() => handleApplyFilters(tempFilters)}
                >
                  Apply
                </button>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationPopup
          show={showDeleteModal}
          onHide={hideDeleteModal}
          itemType="stocktake"
          itemId={stocktakeId}
          itemName="this stock take"
          refreshList={() => {}}
          onSuccess={handleDeleteSuccess}
        />

      </div>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    ...state
  };
}

export default connect(mapStateToProps)(StocktakeDetail);

